import { BubbleMenuPlugin, type BubbleMenuPluginProps } from "@tiptap/extension-bubble-menu";
import { useCurrentEditor } from "@tiptap/react";
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// Modified from https://github.com/ueberdosis/tiptap/blob/develop/packages/react/src/menus/BubbleMenu.tsx

// Needed to do this to remove the tabIndex=0 that is added by the BubbleMenuPlugin which makes the container focusable
// and messes with our own focus trap.

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type ControlledRichTextEditorCustomBubbleMenuProps = Optional<
  Omit<Optional<BubbleMenuPluginProps, "pluginKey">, "element">,
  "editor"
> &
  React.HTMLAttributes<HTMLDivElement>;

export const ControlledRichTextEditorCustomBubbleMenu = React.forwardRef<
  HTMLDivElement,
  ControlledRichTextEditorCustomBubbleMenuProps
>(
  (
    {
      pluginKey = "bubbleMenu",
      editor,
      updateDelay,
      resizeDelay,
      shouldShow = null,
      getReferencedVirtualElement,
      options,
      children,
      ...restProps
    },
    ref,
  ) => {
    const menuEl = useRef(document.createElement("div"));

    if (typeof ref === "function") {
      ref(menuEl.current);
    } else if (ref) {
      ref.current = menuEl.current;
    }

    const { editor: currentEditor } = useCurrentEditor();

    useEffect(() => {
      const bubbleMenuElement = menuEl.current;

      bubbleMenuElement.style.visibility = "hidden";
      bubbleMenuElement.style.position = "absolute";

      if (editor?.isDestroyed || (currentEditor as any)?.isDestroyed) {
        return;
      }

      const attachToEditor = editor || currentEditor;

      if (!attachToEditor) {
        console.warn(
          "BubbleMenu component is not rendered inside of an editor component or does not have editor prop.",
        );
        return;
      }

      const plugin = BubbleMenuPlugin({
        updateDelay,
        resizeDelay,
        editor: attachToEditor,
        element: bubbleMenuElement,
        pluginKey,
        shouldShow,
        getReferencedVirtualElement,
        options,
      });

      attachToEditor.registerPlugin(plugin);

      // Remove tabIndex added by BubbleMenuPlugin
      window.requestAnimationFrame(() => {
        bubbleMenuElement.tabIndex = -1;
      });

      return () => {
        attachToEditor.unregisterPlugin(pluginKey);
        window.requestAnimationFrame(() => {
          if (bubbleMenuElement.parentNode) {
            bubbleMenuElement.parentNode.removeChild(bubbleMenuElement);
          }
        });
      };
    }, [editor, currentEditor]);

    return createPortal(<div {...restProps}>{children}</div>, menuEl.current);
  },
);
